import React, { useContext, useState, useEffect } from "react";
import Log from "../utils/Log";
import {
  scrollToLogin,
  scrollToSignup,
  scrollToConfirmationCode,
  scrollToResetPassword,
} from "../utils/scrollAnimations";
import { UserContext } from "../App";
import styles from "../styles/Login.module.scss";
import SocialLogin from "../components/SocialLogin";
import SignIn from "../components/SignIn";
import Signup from "../components/Signup";
import QueryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { setLoggingInfromClient, setProductType } from "../actions/current";
import currentUserInfo from "../selectors/currentUserInfo";
import selectCurrentState from "../selectors/selectCurrentState";
import { LoginClientType } from "../reducers/models";
import classnames from "classnames";
import { retrieveStripeCheckoutSessionFromPlatform, retrieveXsollaCheckoutSessionFromPlatform } from "../api/subscription";


//Main "Container" for /login page (entry point for client vs web login redirection logic)
export default function Login(props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(currentUserInfo);
  const { userState } = useSelector(selectCurrentState);

  const { hideLogo } = props;
  const { handleLogout, authError } = useContext(UserContext);

  const currentUrl = new URL(window.location.href);
  const urlQuery = QueryString.parse(currentUrl.search);

  const [showSignIn] = useState(false);
  const [, setShowSignup] = useState(false);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMigration, setIsMigration] = useState("false");
  const [curentUserName, setCurrentUserName] = useState("");
  const [fromBBClient, setFromBBClient] = useState(false);
  const [, setHideSignIn] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [confirmationData, setShowConfirmationForm] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const mobileBreakPoint = 768;

  const [appleSignInAvailable, setAppleSignInAvailable] = useState(true);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [checkoutSessionBuildboxUserId, setCheckoutSessionBuildboxUserId] = useState(null);
  const [checkoutSessionOneTimeCode, setCheckoutSessionOneTimeCode] = useState(null);
  const [checkoutSessionEmail, setCheckoutSessionEmail] = useState(null);
  const [checkoutSessionIsGuest, setCheckoutSessionIsGuest] = useState(null);
  const [checkoutSessionName, setCheckoutSessionName] = useState(null);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    handleClientLogout(); // eslint-disable-next-line
  }, [isLoggedIn]);

  useEffect(() => {
    if (showResetPassword) {
      scrollToResetPassword();
    }
  }, [showResetPassword]);

  useEffect(() => {
    if (confirmationData != null) {
      scrollToConfirmationCode();
    }
  }, [confirmationData]);

  function parseStripeSessionData(stripeSessionData) {
    const metadata = stripeSessionData?.metadata;
    if(metadata) {
      setCheckoutSessionBuildboxUserId(metadata.buildboxUserId);
      setCheckoutSessionOneTimeCode(metadata.oneTimeCode);
      setCheckoutSessionIsGuest(metadata.isGuest);
    }
    // find the email; it can be in multiple places
    // check customer_email since that would be something explicitly filled in so probably it's most trustworthy?
    if(stripeSessionData?.customer_email) {
      setCheckoutSessionEmail(stripeSessionData.customer_email);
    }
    // maybe there's customer info
    else if(stripeSessionData?.customer_details?.email) {
      setCheckoutSessionEmail(stripeSessionData.customer_details?.email);
    }
    // those are the only places I've seen it so far but since it seems to vary by flow and payment method
    // more may be discovered in the future...
    else {
      // I guess we don't really NEED the email? we can leave the field there for the user to fill in
      console.warn("could not find email in stripe session data");
    }

    if(stripeSessionData?.customer_details?.name) {
      setCheckoutSessionName(stripeSessionData.customer_details?.name);
    }
  }

  function parseXsollaSessionData(xsollaSessionData) {
    setCheckoutSessionBuildboxUserId(xsollaSessionData.buildboxUserId);
    setCheckoutSessionOneTimeCode(xsollaSessionData.oneTimeCode);
    setCheckoutSessionIsGuest(xsollaSessionData.isGuest);

    setCheckoutSessionEmail(xsollaSessionData.email);

    if(xsollaSessionData.name) {
      setCheckoutSessionName(xsollaSessionData.name);
    }
  }  

  async function onLoad() {
    setIsLoading(true);
    sessionStorage.setItem("fromBBClient", false);

    try {
      // if we're coming after a guest checkout, we gotta do some account link stuff depending on the payment provider
      if(urlQuery["signup"] === "1" && urlQuery["stripeCheckoutSessionId"] && urlQuery["stripeCheckoutSessionId"].length > 0) {
        const stripeCheckoutSessionId = urlQuery["stripeCheckoutSessionId"];
        // prep to redirect to the thank you page afterward
        sessionStorage.setItem("previousRoute", "/thankyou");
        const stripeSessionResponse = await retrieveStripeCheckoutSessionFromPlatform(stripeCheckoutSessionId);
        parseStripeSessionData(stripeSessionResponse.payload.session);
      }
      else if(urlQuery["signup"] === "1" && urlQuery["xsollaCheckoutSessionId"] && urlQuery["xsollaCheckoutSessionId"].length > 0) {
        const xsollaCheckoutSessionId = urlQuery["xsollaCheckoutSessionId"];
        // prep to redirect to the thank you page afterward
        sessionStorage.setItem("previousRoute", "/thankyou");
        const xsollaSessionResponse = await retrieveXsollaCheckoutSessionFromPlatform(xsollaCheckoutSessionId);
        parseXsollaSessionData(xsollaSessionResponse.payload);
      }
      // otherwise we're not in a post-guest-checkout (via normal funnel or delayed email link) situation so just act natural

      if(currentUser) {
        const { username } = currentUser;

        if (username) {
          setCurrentUserName(username);
          setIsLoggedIn(true);
        }
      }
      setIsLoading(false);
    } 
    catch (e) {
      console.error("error in login onLoad: ", e);
      setIsLoggedIn(false);
      setIsLoading(false);
      setIsError(true);
      setErrorMessage("An error occurred while loading the login page. Please try again later or contact support at support@buildbox.com.");
    }

    if (Object.keys(urlQuery).length) {
      setLoginView(urlQuery);
    } 
    else {
      Log.trace("no query params so assuming web login as client always identifies itself if nothing else");
      dispatch(setLoggingInfromClient(LoginClientType.Web));
    }
  }

  const setLoginView = () => {
    const { signup, showForm, "buildbox-login": buildboxLogin, port, migration, view, appleavail } = urlQuery;

    if (signup === "1") {
      setHideSignIn(true);
      if (showForm === "true" || showForm === "1") {
        scrollToSignup(props, windowWidth);
        setShowSignup(true);
      }
    }

    if (buildboxLogin === "1") {
      Log.trace("logging in from bb client");

      dispatch(setLoggingInfromClient(LoginClientType.Client));

      setFromBBClient(true);
      sessionStorage.setItem("fromBBClient", true);

      // handleShowSignIn();

      if (port) {
        Log.trace("websocket port: ", port);
        sessionStorage.setItem("websocketPort", port);
      }
      if (migration) {
        Log.trace("is Legacy User Migration");
        setIsMigration("true");
      }

      /**
       * reads view url param to set signup type
       * TODO: client needs to set &view=bb3 in url for bb3
       */

      if(view) {
        const lowercaseView = view.toLowerCase();
        if (lowercaseView === "bb2") {
          dispatch(setProductType("BB2"));
          sessionStorage.setItem("websocket-product", "BB2");
          sessionStorage.setItem("productType", "BB2");
        } 
        else if(lowercaseView === "bb3") {
          dispatch(setProductType("BB3"));
          sessionStorage.setItem("websocket-product", "BB3");
          sessionStorage.setItem("productType", "BB3");
        }
        else if(lowercaseView === "bb4") {
          dispatch(setProductType("BB4"));
          sessionStorage.setItem("websocket-product", "BB4");
          sessionStorage.setItem("productType", "BB4");
        }      
        else if(lowercaseView === "soundbox") {
          dispatch(setProductType("Soundbox"));
          sessionStorage.setItem("websocket-product", "Soundbox");
          sessionStorage.setItem("productType", "Soundbox");
        }
        else if(lowercaseView === "bbbundle") {
          console.log("view is bbbundle");
          dispatch(setProductType("BBBundle"));
          sessionStorage.setItem("websocket-product", "BBBundle");
          sessionStorage.setItem("productType", "BBBundle");          
        }
        else {
          console.error("unrecognized product: ", urlQuery["view"])
          // may as well default to bb3 as most-generic, though something is almost surely going to break later
          dispatch(setProductType("BB3"));
          // but I guess don't set the sessionStorage since we aren't sure?
        }
      }

      // older buildbox clients DON'T pass the appleavail query param
      if (appleavail === "1") {
        setAppleSignInAvailable(true);
      } 
      else {
        setAppleSignInAvailable(false);
      }
    } 
    else {
      // website login
      dispatch(setLoggingInfromClient(LoginClientType.Web));
    }
  };

  const handleClientLogout = () => {
    if (urlQuery["buildbox-login"] === "1" && urlQuery["force"] && isLoggedIn) {
      Log.trace("Forcing logout via BB Client: ", urlQuery["force"]);
      handleSignout();
    }
  };

  const handleSignout = () => {
    //preserve client vs web flow by sending in the original url with any params
    handleLogout(window.location.href);
  };

  let isBuildboxClient = false;
  if (urlQuery["buildbox-login"] === "1") {
    isBuildboxClient = true;
  }

  const SocialSignIn = () => {
    if (urlQuery["utm_source"] === "tiktok") {
      return null;
    }
    return (
      <div className={styles.socialLoginContainer}>
        <SocialLogin
          showSignIn={showSignIn}
          isBuildboxClient={isBuildboxClient}
          appleSignInAvailable={appleSignInAvailable}
        />
      </div>
    );
  };
  const Logo = () => {
    const StackedBB4LogoWhite =
      "https://frontend-assets.buildbox.com/Bbox_4_Logo_Horizontal.svg";
    const StackedBB4LogoBlack =
      "https://frontend-assets.buildbox.com/Bbox_4_Logo_Horizontal_blacktext.svg";    
    const StackedBB3LogoWhite =
      "https://frontend-assets.buildbox.com/Bbox_3_Logo_Horizontal.svg";
    const StackedBB3LogoBlack =
      "https://frontend-assets.buildbox.com/buildbox1_a+1.png";
    const StackedBB2LogoWhite =
      "https://frontend-assets.buildbox.com/Bbox_Classic_Logo_Horizontal.svg";
    const StackedBB2LogoBlack =
      "https://frontend-assets.buildbox.com/BBClassic_Logo_Horizontal_Light.png";
    const StackedSoundboxLogoWhite =
      "https://frontend-assets.buildbox.com/soundbox/Soundbox_Logo_Horizontal.svg";
    const StackedSoundboxLogoBlack =
      "https://frontend-assets.buildbox.com/soundbox/Soundbox_Logo_Horizontal_Light.png";      
    const product = userState?.productType
    const sessionProductType = sessionStorage.getItem("productType") ? sessionStorage.getItem("productType").toLowerCase() : null;
    let logoSrc;

    if ((product && product.toLowerCase() === "bb2") || sessionProductType === "bb2") {
      if (windowWidth >= mobileBreakPoint) {
        logoSrc = StackedBB2LogoWhite;
      } else {
        logoSrc = StackedBB2LogoBlack;
      }
    } 
    else if((product && product.toLowerCase() === "soundbox") || sessionProductType === "soundbox") {
      if (windowWidth >= mobileBreakPoint) {
        logoSrc = StackedSoundboxLogoWhite;
      } else {
        logoSrc = StackedSoundboxLogoBlack;
      }
    }
    else if((product && product.toLowerCase() === "bb3") || sessionProductType === "bb3") {
      if (windowWidth >= mobileBreakPoint) {
        logoSrc = StackedBB3LogoWhite;
      } else {
        logoSrc = StackedBB3LogoBlack;
      }
    }
    else if((product && product.toLowerCase() === "bb4") || sessionProductType === "bb4") {
      if (windowWidth >= mobileBreakPoint) {
        logoSrc = StackedBB4LogoWhite;
      } else {
        logoSrc = StackedBB4LogoBlack;
      }
    }
    else if((product && product.toLowerCase() === "bbbundle") || sessionProductType === "bbbundle") {
      // TODO: bbbundle logo?
      if (windowWidth >= mobileBreakPoint) {
        logoSrc = StackedBB4LogoWhite;
      } else {
        logoSrc = StackedBB4LogoBlack;
      }
    }      
    else {
      console.log("unknown product, defaulting to BB4")
      if (windowWidth >= mobileBreakPoint) {
        logoSrc = StackedBB4LogoWhite;
      } else {
        logoSrc = StackedBB4LogoBlack;
      }      
    }
    const logoClassName = classnames({
      [styles.logo]: true,
      [styles.client]: true,
    });
    return (
      <img className={logoClassName} src={logoSrc} alt="Buildbox Logo"></img>
    );
  };

  if (isLoading) {
    // TODO: find a better loading element
    return (
      <div className={styles.BBClientLogin}>
        <span className={styles.clientLoginText}>Loading...</span>
      </div>
    );
  }

  if(isError) {
    return (
      <div className={styles.BBClientLogin}>
        <span className={styles.clientLoginText}>{errorMessage}</span>
      </div>
    );
  }

  return (
    <div className={styles.BBClientLogin}>
      {!hideLogo && <Logo />}
      <div className={styles.loginContainerDefault}>
        <div className={styles.clientLoginFormContainer} id={"expandable"}>
          <div className={styles.authLoginContainer}>
            <SignIn
              router={props}
              authError={authError}
              fromBBClient={fromBBClient}
              appleSignInAvailable={appleSignInAvailable}
              className={showSignIn ? styles.showContent : styles.content}
              setShowResetPassword={setShowResetPassword}
              showResetPassword={showResetPassword}
              scrollToSignup={() => scrollToSignup(props, windowWidth)}
              isLoggedIn={isLoggedIn}
              curentUserName={curentUserName}
              handleSignout={handleSignout}
              windowWidth={windowWidth}
            />
            <Signup
              router={props}
              isMigration={isMigration}
              fromBBClient={fromBBClient}
              scrollToLogin={() => scrollToLogin(props, windowWidth)}
              setShowConfirmationForm={setShowConfirmationForm}
              confirmationData={confirmationData}
              accountlessCheckoutSessionInfo={
                (checkoutSessionBuildboxUserId || checkoutSessionOneTimeCode || checkoutSessionIsGuest || checkoutSessionEmail || checkoutSessionName) ?
                {
                  buildboxUserId: checkoutSessionBuildboxUserId,
                  oneTimeCode: checkoutSessionOneTimeCode,
                  isGuest: checkoutSessionIsGuest,
                  email: checkoutSessionEmail,
                  name: checkoutSessionName,
                } : null
              }
            />
          </div>
        </div>
        {!showResetPassword && <SocialSignIn />}
      </div>
    </div>
  );
}
